import { useMutation, useQuery } from '@apollo/client'
import { Alert } from '@designsforhealth/dfh-react-components'
import PropTypes from 'prop-types'
import React, { useCallback, useContext } from 'react'
import styled from 'styled-components'

import {
  DeleteCustomerAddressDocument,
  GetCustomerAddressesDocument,
} from '../../../../graphql/magento'
import useLogAndCaptureError from '../../../../hooks/useLogAndCaptureError'
import { TokenContext } from '../../../../layouts/context'
import { tokenHasMagentoUserScope } from '../../../../lib/auth/scope'
import { usePageUrl } from '../../../../lib/pages'
import ContactSupportMessage from '../../../ContactSupportMessage'
import ErrorAccessingMagentoMessage from '../../../ErrorAccessingMagentoMessage'
import Spacer from '../../../Spacer'
import Spinner from '../../../Spinner'
import HeaderWithSecondaryButton from '../HeaderWithSecondaryButton'
import AddressBook from './AddressBook'
import EmptyAddressBook from './EmptyAddressBook'

const AddressBookContainer = ({ className }) => {
  const token = useContext(TokenContext)
  const { navigate } = usePageUrl()
  const hasMagentoUserScope = tokenHasMagentoUserScope(token)

  const { data, loading, refetch } = useQuery(GetCustomerAddressesDocument, {
    context: { token },
    skip: !token || !hasMagentoUserScope,
  })

  const [deleteCustomerAddress, { error: deleteError }] = useMutation(
    DeleteCustomerAddressDocument,
    {
      context: { token },
      onCompleted() {
        refetch()
      },
    },
  )

  useLogAndCaptureError(deleteError)
  const onDeleteCustomerAddress = useCallback(
    (id) =>
      deleteCustomerAddress({
        variables: {
          id,
        },
      }),
    [deleteCustomerAddress],
  )

  if (!hasMagentoUserScope) {
    return (
      <div className={className}>
        <Alert type="error">
          <ErrorAccessingMagentoMessage />
        </Alert>
      </div>
    )
  }

  if (loading || !data) return <Spinner loading={loading} />

  const addresses = data?.customer?.addresses

  return (
    <div className={className}>
      <HeaderWithSecondaryButton
        buttonText="Add New Address"
        headerText="Address Book"
        onClick={() => navigate('/account/addresses/new')}
      />
      <Spacer size={3} />
      {deleteError && (
        <Alert type="error">
          An error occurred deleting the address. Please try again. <ContactSupportMessage />
        </Alert>
      )}
      {addresses.length ? (
        <AddressBook addresses={addresses} onDeleteCustomerAddress={onDeleteCustomerAddress} />
      ) : (
        <EmptyAddressBook />
      )}
    </div>
  )
}

AddressBookContainer.defaultProps = {
  className: '',
}

AddressBookContainer.propTypes = {
  className: PropTypes.string,
}

const StyledAddressBookContainer = styled(AddressBookContainer)({
  fontSize: '15px',
  lineHeight: '16px',
})

export default StyledAddressBookContainer
